import { WorkfrontBundleState } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Badge } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import { formatUtc } from '@/lib/format-utc'
import type { WorkfrontHealthDTO, WorkfrontOverviewDTO, WorkfrontRunDTO } from '@/services/workfront.service'
import { humanize, RunStatusBadge } from './StatusBadges'

export interface OverviewTilesProps {
	overview: WorkfrontOverviewDTO
	/** Null while the health check is in flight or when it could not be read. */
	health: WorkfrontHealthDTO | null
}

/** The order the states read in: the ones needing a look first. */
const STATE_ORDER = [
	WorkfrontBundleState.AMBIGUOUS,
	WorkfrontBundleState.AWAITING_PARENT,
	WorkfrontBundleState.ACTIVE,
	WorkfrontBundleState.CLOSED,
	WorkfrontBundleState.OUT_OF_SCOPE,
	WorkfrontBundleState.REMOVED,
]

/** A label and its value. A long value (a host, a user's name) wraps rather than being cut off. */
function Line({ label, children }: { label: ReactNode; children: ReactNode }) {
	return (
		<div className="flex items-baseline justify-between gap-4 py-1 text-ds-sm">
			<span className="shrink-0 text-ds-text-muted">{label}</span>
			<span className="min-w-0 text-right wrap-anywhere">{children}</span>
		</div>
	)
}

function HealthTile({ health }: { health: WorkfrontHealthDTO | null }) {
	return (
		<SectionCard title="Connection">
			{!health ? (
				<p className="text-ds-sm text-ds-text-muted">Checking Workfront…</p>
			) : (
				<>
					<Line label="Workfront">
						<Badge size="sm" variant={health.authenticated ? 'success' : 'danger'}>
							{health.authenticated ? 'connected' : 'not connected'}
						</Badge>
					</Line>
					<Line label="Host">{health.host}</Line>
					{health.userName ? <Line label="Technical user">{health.userName}</Line> : null}
					{health.errorMessage ? <p className="mt-2 text-ds-sm text-ds-danger">{health.errorMessage}</p> : null}
				</>
			)}
		</SectionCard>
	)
}

function LastRun({ label, run }: { label: string; run: WorkfrontRunDTO | null }) {
	return (
		<div className="py-1 text-ds-sm">
			<div className="flex items-center justify-between gap-4">
				<span className="shrink-0 text-ds-text-muted">{label}</span>
				{run ? <RunStatusBadge status={run.status} /> : <span className="text-ds-text-muted">never</span>}
			</div>
			{run ? <p className="mt-0.5 text-ds-text-muted">{formatUtc(run.startedAt)}</p> : null}
		</div>
	)
}

/**
 * The adapter at a glance: the connection, the bundles by state, the open
 * problems by step, and when reference data and discovery last ran. Prop-driven;
 * the overview page owns the fetching.
 */
export function OverviewTiles({ overview, health }: OverviewTilesProps) {
	const states = STATE_ORDER.filter((state) => (overview.bundlesByState[state] ?? 0) > 0)
	const steps = Object.entries(overview.openErrorsByStep)
	return (
		<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
			<HealthTile health={health} />
			<SectionCard title="Bundles">
				{states.length === 0 ? <p className="text-ds-sm text-ds-text-muted">No bundles yet.</p> : null}
				{states.map((state) => (
					<Line key={state} label={humanize(state)}>
						{overview.bundlesByState[state]}
					</Line>
				))}
			</SectionCard>
			<SectionCard title="Open problems">
				{steps.length === 0 ? <p className="text-ds-sm text-ds-text-muted">Nothing open.</p> : null}
				{steps.map(([step, counts]) => (
					<Line key={step} label={humanize(step)}>
						<span className="flex gap-1">
							{counts.error > 0 ? (
								<Badge size="sm" variant="danger">{`${counts.error} ${counts.error === 1 ? 'error' : 'errors'}`}</Badge>
							) : null}
							{counts.warning > 0 ? (
								<Badge
									size="sm"
									variant="warning"
								>{`${counts.warning} ${counts.warning === 1 ? 'warning' : 'warnings'}`}</Badge>
							) : null}
						</span>
					</Line>
				))}
			</SectionCard>
			<SectionCard title="Last runs">
				<LastRun label="Discovery" run={overview.lastDiscovery} />
				<LastRun label="Reference data" run={overview.lastReferenceRefresh} />
			</SectionCard>
		</div>
	)
}
